import { Typography, ListItem, ListItemText, Box } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { ThemePalette } from 'mui.theme';
import { useEffect } from 'react';
import {
  convertBasedOnUnitSystem,
  convertLitersToGallons,
  getLabelWrtUnitSystem,
} from 'utils/helpers/unitSystem';
import { isAllValuesZero } from 'views/Utilization/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface SuggestionsData {
  haulers: {
    totalFuel: number;
    idleFuel: number;
    idleFuelPercentage: number;
  };
  loaders: {
    totalFuel: number;
    idleFuel: number;
    idleFuelPercentage: number;
  };
  totalFleet: {
    totalFuel: number;
    idleFuel: number;
    idleFuelPercentage: number;
  };
}
interface Props {
  data: SuggestionsData;
  assetType: string;
  setSuggestionData: any;
}

const RenderFuelSavingsMessage = ({
  name,
  percentage,
}: {
  name: string;
  percentage: number;
}) => {
  const { t } = useTranslation();
  return (
    <p>
      {t('utilization_possible_fuel_savings', {
        name,
        percentage: percentage.toFixed(2),
      })}
    </p>
  );
};

const RenderFactsMessage = ({
  name,
  totalFuel,
  idleFuel,
}: {
  name: string;
  totalFuel: number;
  idleFuel: number;
}) => {
  const { t } = useTranslation();
  const { unitSystem } = useCustomerInfo();
  return (
    <Box sx={{ marginLeft: 2 }}>
      {' '}
      {name}
      <Box sx={{ marginLeft: 2 }}>
        <p>
          {t('total_fuel')} {(totalFuel || 0).toFixed(2)}{' '}
          {getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}
        </p>
        <p>
          {t('idle_fuel')} {(idleFuel || 0).toFixed(2)}{' '}
          {getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}
        </p>
      </Box>
    </Box>
  );
};

const RenderSavingsBasedOnAssetType = ({
  assetType,
  data,
}: {
  assetType: string;
  data: SuggestionsData;
}) => {
  const { t } = useTranslation();
  switch (assetType) {
    case 'all':
      return (
        <>
          {!isAllValuesZero(data.totalFleet) ? (
            <RenderFuelSavingsMessage
              name={t('total_fleet')}
              percentage={data.totalFleet.idleFuelPercentage}
            />
          ) : null}
          {!isAllValuesZero(data.haulers) ? (
            <RenderFuelSavingsMessage
              name={t('haulers')}
              percentage={data.haulers.idleFuelPercentage}
            />
          ) : null}
          {!isAllValuesZero(data.loaders) ? (
            <RenderFuelSavingsMessage
              name={t('loaders')}
              percentage={data.loaders.idleFuelPercentage}
            />
          ) : null}
        </>
      );
    case 'hauler':
      return (
        <RenderFuelSavingsMessage
          name={t('haulers')}
          percentage={data.haulers.idleFuelPercentage}
        />
      );
    case 'loader':
      return (
        <RenderFuelSavingsMessage
          name={t('loaders')}
          percentage={data.loaders.idleFuelPercentage}
        />
      );
    default:
      return <p>{t('no_valid_asset_type_selected')}</p>;
  }
};

const RenderSectionByAssetType = ({
  assetType,
  data,
}: {
  assetType: string;
  data: SuggestionsData;
}) => {
  const { t } = useTranslation();
  switch (assetType) {
    case 'all':
      return (
        <>
          {!isAllValuesZero(data.totalFleet) ? (
            <RenderFactsMessage
              name={t('total_fleet')}
              totalFuel={data.totalFleet.totalFuel}
              idleFuel={data.totalFleet.idleFuel}
            />
          ) : null}
          {!isAllValuesZero(data.haulers) ? (
            <RenderFactsMessage
              name={t('haulers')}
              totalFuel={data.haulers.totalFuel}
              idleFuel={data.haulers.idleFuel}
            />
          ) : null}
          {!isAllValuesZero(data.loaders) ? (
            <RenderFactsMessage
              name={t('loaders')}
              totalFuel={data.loaders.totalFuel}
              idleFuel={data.loaders.idleFuel}
            />
          ) : null}
        </>
      );
    case 'hauler':
      return !isAllValuesZero(data.haulers) ? (
        <RenderFactsMessage
          name={t('haulers')}
          totalFuel={data.haulers.totalFuel}
          idleFuel={data.haulers.idleFuel}
        />
      ) : null;
    case 'loader':
      return !isAllValuesZero(data.loaders) ? (
        <RenderFactsMessage
          name={t('loaders')}
          totalFuel={data.loaders.totalFuel}
          idleFuel={data.loaders.idleFuel}
        />
      ) : null;
    default:
      return <p>{t('no_valid_asset_type_selected')}</p>;
  }
};

const SuggestionItems = ({ data, assetType, setSuggestionData }: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const { unitSystem } = useCustomerInfo();

  useEffect(() => {
    setSuggestionData({
      facts: {
        'Fleet Total Fuel': `${convertBasedOnUnitSystem(
          convertLitersToGallons,
          data.totalFleet.totalFuel || 0,
          unitSystem
        ).toFixed(2)} ${getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}`,
        'Fleet Idle Fuel': `${convertBasedOnUnitSystem(
          convertLitersToGallons,
          data.totalFleet.idleFuel || 0,
          unitSystem
        ).toFixed(2)} ${getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}`,
        'Haulers Total Fuel': `${convertBasedOnUnitSystem(
          convertLitersToGallons,
          data.haulers.totalFuel || 0,
          unitSystem
        ).toFixed(2)} ${getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}`,
        'Haulers Idle Fuel': `${convertBasedOnUnitSystem(
          convertLitersToGallons,
          data.haulers.idleFuel || 0,
          unitSystem
        ).toFixed(2)} ${getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}`,
        'Loaders Total Fuel': `${convertBasedOnUnitSystem(
          convertLitersToGallons,
          data.loaders.totalFuel || 0,
          unitSystem
        ).toFixed(2)} ${getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}`,
        'Loaders Idle Fuel': `${convertBasedOnUnitSystem(
          convertLitersToGallons,
          data.loaders.idleFuel || 0,
          unitSystem
        ).toFixed(2)} ${getLabelWrtUnitSystem('L', unitSystem).toLowerCase()}`,
      },
      recommendations: {
        'Possible fuel savings for Total Fleet': `${data.totalFleet.idleFuelPercentage.toFixed(
          2
        )}%`,
        'Possible fuel savings for Haulers': `${data.haulers.idleFuelPercentage.toFixed(
          2
        )}%`,
        'Possible fuel savings for Loaders': `${data.loaders.idleFuelPercentage.toFixed(
          2
        )}%`,
      },
    });
  }, [data]);

  return (
    <>
      {data && !Object.values(data).every(isAllValuesZero) ? (
        <>
          <Typography
            sx={{
              color: enableDarkTheme
                ? '#b0b7c3'
                : ThemePalette.typography.secondary,
              fontSize: '10px',
              paddingTop: '2px',
            }}
          >
            <i>{t('based_on_idling')}</i>
          </Typography>
          {/* Facts Section */}
          <ListItem
            sx={{
              borderBottom: `1px solid ${
                enableDarkTheme ? _palette.dark.borderGrey : '#D9DADA'
              }`,
              paddingBottom: '16px',
              display: 'flex',
              direction: 'row',
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: (enableDarkTheme ? _palette.dark : _palette.light)
                    .textPrimary,
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                {t('utilization_facts')}
              </Typography>
              <Box
                sx={{
                  color: enableDarkTheme
                    ? '#b0b7c3'
                    : ThemePalette.typography.secondary,
                  fontSize: '12px',
                  paddingTop: '5px',
                }}
              >
                <p>{t('fuel_consumption_2')}</p>
                <RenderSectionByAssetType assetType={assetType} data={data} />
              </Box>
            </Box>
          </ListItem>
          {/* Suggestions Section */}
          <ListItem
            sx={{
              borderBottom: `1px solid ${
                enableDarkTheme ? _palette.dark.borderGrey : '#D9DADA'
              }`,
              paddingBottom: '16px',
              display: 'flex',
              direction: 'row',
            }}
          >
            <ListItemText
              primary={
                <Typography
                  sx={{
                    color: (enableDarkTheme ? _palette.dark : _palette.light)
                      .textPrimary,
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {t('utilization_recommendations')}
                </Typography>
              }
              secondary={
                <Typography
                  sx={{
                    color: enableDarkTheme
                      ? '#b0b7c3'
                      : ThemePalette.typography.secondary,
                    fontSize: '12px',
                    paddingTop: '5px',
                  }}
                >
                  <RenderSavingsBasedOnAssetType
                    assetType={assetType}
                    data={data}
                  />
                </Typography>
              }
            />
          </ListItem>
        </>
      ) : (
        <Typography
          sx={{
            color: enableDarkTheme
              ? '#b0b7c3'
              : ThemePalette.typography.secondary,
            fontSize: '12px',
            paddingTop: '5px',
          }}
        >
          {t('no_data_available_3')}
        </Typography>
      )}
    </>
  );
};

export default SuggestionItems;
